import React, { useState, useEffect } from 'react'
import { ArrowRight, Twitter, Gamepad, Sailboat, Zap, Globe } from 'lucide-react'
import WalletModal from './WalletModal'
import axios from 'axios'
import { storage } from '../../core/storage'
import { useLaserEyes, XVERSE } from '@omnisat/lasereyes-react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

const Home = () => {
  const [content, setContent] = useState('worlds')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [userWorlds, setUserWorlds] = useState([])
  const [, setStatus] = useState(false)
  const [worlds, setWorlds] = useState([])
  const [loading, setLoading] = useState(true)
  const [userWorld, setUserWorld] = useState('')
  const { connect, disconnect, connected, address, publicKey, provider, signMessage } = useLaserEyes()
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    axios
      .get(`${env.PUBLIC_API_URL}/worlds`)
      .then(res => {
        setWorlds(res.data.worlds || []) // always use actual data
      })
      .catch(err => {
        console.error('Failed to fetch worlds:', err)
      })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (connected) {
      checkOrdinals()
    }
  }, [connected])

  useEffect(() => {
    const connectWalletAndRedirect = async () => {
      if (location.pathname === '/xverse/connect') {
        try {
          if (!connected) {
            await connect(XVERSE)
          }
          navigate('/')
        } catch (error) {
          console.error('Wallet connection failed:', error)
          navigate('/')
        }
      }
    }
    connectWalletAndRedirect()
  }, [location.pathname, connect, navigate, connected])

  if (loading) {
    return <div className='text-center py-20 text-gray-500'>Loading...</div>
  }

  const checkOrdinals = async () => {
    if (!address) {
      setStatus('❌ Wallet not connected. Please connect first.')
      return
    }

    try {
      const nonceResponse = await fetch(`${env.PUBLIC_API_URL}/get-nonce`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallet: address }),
      })

      const { nonce } = await nonceResponse.json()
      const signature = await signMessage(nonce, address)
      const res = await axios.post(`${env.PUBLIC_API_URL}/check-ordinal`, {
        walletAddress: address,
        signature,
        publicKey,
        provider,
      })

      if (res.data.success) {
        storage.set('authToken', res.data.accessToken)
        storage.set('bitmaps', JSON.stringify(res.data.bitmaps))

        const mappedUserWorlds = res.data.bitmaps.map(bm => ({
          _id: bm.inscriptionId,
          name: bm.name,
          title: bm.name,
          description: 'Experience the future of social interactions, gaming, and entertainment.',
          thumbnail: 'https://d22adnnyocrgps.cloudfront.net/assets/bitverse-default.png',
        }))

        setUserWorlds(mappedUserWorlds)
        setUserWorld(mappedUserWorlds?.[0]?.name?.split('.')?.[0] || '')

        setStatus('✅ Verified - Role Updated')
      } else {
        setStatus('❌ No valid Ordinals found.')
      }
    } catch (error) {
      console.error(error)
      setStatus('❌ Error checking Ordinals')
    }
  }

  return (
    <div className='w-full min-h-screen bg-gradient-to-br from-primary to-secondary text-white text-center font-sans'>
      <div className='flex items-center justify-between px-6 sm:px-20 py-4 bg-black-20 backdrop-blur-lg shadow-md border-b border-white-10'>
        <div className='flex items-center gap-2'>
          <img src='/Bitverse.png' alt='Bitverse Logo' className='h-12 w-auto object-contain' />
        </div>
        <div className='flex items-center gap-6'>
          <button
            className='bg-gradient-to-br from-btnPrimary to-btnSecondary text-white px-5 py-2 rounded-full text-base transition-all'
            onClick={() => {
              if (connected) {
                disconnect()
                storage.remove('authToken')
                storage.remove('bitmaps')
              } else {
                setIsModalOpen(true)
              }
            }}
          >
            {connected ? `${address.slice(0, 6)}...${address.slice(-4)}` : 'Connect'}
          </button>
        </div>
      </div>

      <div className='flex flex-col items-center justify-center min-h-60vh px-6 sm:px-16'>
        <h1 className='text-5xl sm:text-7xl font-extrabold mb-4 leading-[1.1] text-white'>
          Discover. Create. Explore.
        </h1>
        <p className='text-lg sm:text-xl text-gray-400 mb-6 max-w-lg'>
          Enter the next dimension of the metaverse where imagination meets reality.
        </p>
        <a href={`/world/${userWorld}`} className='inline-block'>
          <button className='bg-gradient-to-br from-btnPrimary to-btnSecondary px-8 py-3 rounded-full text-lg mt-4 text-white transition-all shadow-md hover:shadow-lg'>
            Get Started
          </button>
        </a>
      </div>

      <div className='flex items-center justify-center gap-6 text-lg border-white/10 pb-4'>
        <button
          className={`py-2 px-6 rounded-full ${content === 'worlds' ? 'bg-gradient-to-br from-btnPrimary to-btnSecondary text-white' : 'bg-transparent text-white/50'}`}
          onClick={() => setContent('worlds')}
        >
          Worlds
        </button>
        <button
          className={`py-2 px-6 rounded-full ${content === 'events' ? 'bg-gradient-to-br from-btnPrimary to-btnSecondary text-white' : 'bg-transparent text-white/50'}`}
          onClick={() => setContent('events')}
        >
          Events
        </button>
      </div>

      {connected && userWorlds.length > 0 && (
        <div className='px-8 sm:px-24 py-8'>
          <h2 className='text-2xl font-semibold text-white mb-4'>Your Worlds</h2>
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
            {userWorlds.map((world, index) => (
              <Link key={world._id || index} to={`/world/${world.name}`}>
                <div className='bg-muted rounded-2xl hover:scale-105 transition-transform duration-300 cursor-pointer shadow-lg hover:shadow-xl'>
                  <div className='w-full h-48 relative'>
                    <img src={world.thumbnail} alt={world.name} className='w-full h-full object-cover rounded-t-2xl' />
                  </div>
                  <div className='p-4 text-left'>
                    <h3 className='text-lg font-semibold text-accent'>{world.title}</h3>
                    <p className='text-gray-400 text-sm mt-1'>{world.description}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {content === 'worlds' && (
        <div className='px-8 sm:px-24 py-8'>
          <h2 className='text-2xl font-semibold text-white mb-4'>Worlds</h2>
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
            {worlds.length > 0 ? worlds.map((world, index) => (
              <Link key={world.id || index} to={`/world/${world.name}`}>
                <div className='bg-muted rounded-2xl hover:scale-105 transition-transform duration-300 cursor-pointer shadow-lg hover:shadow-xl'>
                  <div className='w-full h-48 relative'>
                    <img src='https://d22adnnyocrgps.cloudfront.net/assets/bitverse-default.png' alt={world.name} className='w-full h-full object-cover rounded-t-2xl' />
                  </div>
                  <div className='p-4 text-left'>
                    <h3 className='text-lg font-semibold text-accent'>{world.name}</h3>
                    <p className='text-gray-400 text-sm mt-1'>Experience the future of social interactions, gaming, and entertainment.</p>
                  </div>
                </div>
              </Link>
            )) : (
              <div className='text-gray-500 col-span-full text-center py-10'>
                No worlds available.
              </div>
            )}
          </div>
        </div>
      )}

      {content === 'events' && (
        <div className='px-8 sm:px-24 py-12'>
          <div className='text-left'>
            <h2 className='text-2xl font-semibold text-white'>Upcoming Events</h2>
            <div className='mt-6 p-5 bg-muted rounded-2xl shadow-lg hover:scale-105 transition-all'>
              <div className='flex items-center gap-2 text-danger text-sm font-medium'>
                <Zap />
                <div>Live Now</div>
              </div>
              <h3 className='uppercase text-xl font-bold mt-2 text-accent'>Metaverse Summit</h3>
              <p className='text-base text-gray-400 mt-2'>
                Join top creators and innovators for an immersive experience in the future of digital spaces.
              </p>
              <div className='flex items-center justify-between mt-4'>
                <div className='flex items-center gap-2'>
                  <Globe className='text-accent text-lg' />
                  <div className='text-white'>mybitverse.com/summit</div>
                </div>
                <ArrowRight className='text-borderColor group-hover:text-accent transition-all duration-300' />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className='border-t border-white-10 bg-black-20 py-6 px-8 sm:px-20 flex flex-col sm:flex-row items-center justify-between'>
        <div className='flex items-center gap-4 text-white/80 text-sm'>
          <span>© 2025 Bitverse. All rights reserved.</span>
        </div>
        <div className='flex items-center gap-6'>
          <Sailboat className='cursor-pointer text-white-80 hover:text-accent transition-all' />
          <Gamepad className='cursor-pointer text-white-80 hover:text-accent transition-all' />
          <Twitter className='cursor-pointer text-white-80 hover:text-accent transition-all' />
        </div>
      </div>

      <WalletModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  )
}

export default Home
